import { useState, useMemo } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Calculator } from "lucide-react";
import { useNavigate } from "react-router-dom";

const resultInitial = { opacity: 0.5, scale: 0.95 };
const resultAnimate = { opacity: 1, scale: 1 };

export function ROIAnchor() {
  const reduced = useReducedMotion();
  const navigate = useNavigate();
  const [clientValue, setClientValue] = useState(2000);
  const [adSpend, setAdSpend] = useState(1000);

  const clientsNeeded = useMemo(() => {
    if (clientValue <= 0) return 0;
    return Math.ceil((3500 + adSpend) / clientValue);
  }, [clientValue, adSpend]);

  return (
    <section id="roi-calculator" className="py-8 md:py-12">
      <div className="max-w-xl mx-auto px-4">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 mb-2">
            <Calculator className="w-5 h-5 text-primary" />
            <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-medium">ROI Calculator</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground tracking-tight">
            How many clients to break even?
          </h2>
        </div>

        <div className="border border-border rounded-2xl bg-card p-6 shadow-sm space-y-5">
          <InputSlider label="Average client value" prefix="$" value={clientValue} onChange={setClientValue} min={500} max={25000} step={250} />
          <InputSlider label="Monthly ad spend" prefix="$" value={adSpend} onChange={setAdSpend} min={500} max={10000} step={250} />

          <div className="h-px bg-border" />

          <div className="text-center py-4">
            <motion.p
              key={clientsNeeded}
              initial={reduced ? {} : resultInitial}
              animate={reduced ? {} : resultAnimate}
              transition={{ duration: 0.3 }}
              className="text-5xl md:text-6xl font-bold text-primary will-change-transform"
            >
              {clientsNeeded}
            </motion.p>
            <p className="text-sm text-muted-foreground mt-1">clients needed to see a positive return</p>
          </div>

          <p className="text-[10px] text-muted-foreground/70 text-center">
            This is an estimate. Your results will vary based on your market, practice area, and other factors.
          </p>

          <div className="text-center">
            <motion.button
              whileHover={reduced ? {} : { scale: 1.05 }}
              whileTap={reduced ? {} : { scale: 0.97 }}
              className="bg-primary text-primary-foreground font-semibold text-sm sm:text-base px-6 sm:px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-shadow max-w-[280px] sm:max-w-none mx-auto leading-tight"
              onClick={() => navigate("/audit")}
            >
              See What This Looks Like for Your Firm
            </motion.button>
          </div>
        </div>
      </div>
    </section>
  );
}

function InputSlider({
  label, value, onChange, min, max, step, prefix = "", suffix = "",
}: {
  label: string; value: number; onChange: (v: number) => void;
  min: number; max: number; step: number; prefix?: string; suffix?: string;
}) {
  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <label className="text-sm font-medium text-foreground">{label}</label>
        <span className="text-sm font-semibold text-primary">{prefix}{value.toLocaleString()}{suffix}</span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-2 bg-muted rounded-full appearance-none cursor-pointer accent-primary [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:appearance-none"
      />
    </div>
  );
}
